import SidebarItem from '../SidebarItem';
import { Box, Fade, List } from '@mui/material';
import { SwipeableDrawer } from '@mui/material';
import { default as MuiDrawer } from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useSidebarItems } from 'hooks/useSidebarItems';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const drawerWidth = 240;
const drawerMarginTop = 56;

const openedMixin = (theme) => ({
  width: drawerWidth,
  marginTop: drawerMarginTop,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  marginTop: drawerMarginTop,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.up('sm')]: {
    width: 0,
  },
  [theme.breakpoints.down('sm')]: {
    width: 0,
  },
});

const StyledDiv = styled('div')(() => ({
  display: 'flex',
  overflow: 'hidden',
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  marginTop: drawerMarginTop,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

// COMPONENT
export default function Sidebar({ open, setOpen }) {
  const { sidebarState } = useSidebarItems();
  const { isDesktop } = useBreakpoint();
  const location = useLocation();

  useEffect(() => {
    !sidebarState.items?.length || !isDesktop ? setOpen?.(false) : setOpen?.(true);
  }, [sidebarState.items, setOpen, isDesktop]);

  useEffect(() => {
    document.body.style.overflow = ''; // workaround for SwipeableDrawer not removing overflow: hidden on body
  }, [location.pathname]);

  if (!sidebarState.items?.length) {
    return null;
  }

  const handleDrawerClick = () => {
    if (!open) {
      setOpen(true);
    }
  };

  return (
    <Fade in>
      <StyledDiv>
        {isDesktop && (
          <Drawer variant="permanent" open={open} onClick={handleDrawerClick}>
            <DrawerContent open={open} sidebarState={sidebarState} handleDrawerClick={handleDrawerClick} />
          </Drawer>
        )} 
        {!isDesktop && open && ( // workaround for SwipeableDrawer: removing it from DOM when closed since sometimes it leaves invisible backdrop behind after navigation 
          <SwipeableDrawer
            variant="temporary"
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            ModalProps={{
              sx: {
                zIndex: (theme) => theme.zIndex.modal,
              },
            }}
            PaperProps={{
              sx: {
                width: drawerWidth,
                zIndex: (theme) => theme.zIndex.modal,
              },
            }}
          >
            <DrawerContent open={open} sidebarState={sidebarState} handleDrawerClick={handleDrawerClick} />
          </SwipeableDrawer>
        )}
      </StyledDiv>
    </Fade>
  );
}

const DrawerContent = ({ open, sidebarState, handleDrawerClick }) => {
  return (
    <List component="nav" disablePadding>
      {open && sidebarState.sidebarHeader}
      <Box sx={{ pb: '5em' }}>
        {sidebarState.items
          .filter((item) => !!item)
          .map((item, index) => {
            return <SidebarItem {...item} openSidebar={handleDrawerClick} isSidebarOpen={open} key={index} />;
          })}
      </Box>
    </List>
  );
};